import { useId } from 'react';

import { Button } from './Button';
import { Spinner } from './Spinner';

export interface FileHandlerWidgetProps {
  mode: 'upload' | 'download';
  fileName?: string;
  fileSize?: string;
  acceptedTypes?: string[];
  progress?: number;
  status?: 'prompt' | 'uploading' | 'complete' | 'error' | 'available';
  errorMessage?: string;
  onFileSelect?: (file: File) => void;
  onDownload?: () => void;
  onRetry?: () => void;
  className?: string;
}

export function FileHandlerWidget({
  mode,
  fileName,
  fileSize,
  acceptedTypes = [],
  status = mode === 'upload' ? 'prompt' : 'available',
  errorMessage,
  onFileSelect,
  onDownload,
  onRetry,
  className = '',
}: FileHandlerWidgetProps) {
  const inputId = useId();

  if (status === 'error') {
    return (
      <div className={`p-sd-2 rounded-sd-md bg-sd-error/10 w-full ${className}`.trim()}>
        <div className="flex items-center gap-sd-1">
          <svg className="w-4 h-4 text-sd-error shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 4L12 12M12 4L4 12" />
          </svg>
          <span className="text-sd-sm text-sd-error">{errorMessage || "That file didn't go through — try again or use a smaller file"}</span>
        </div>
        {onRetry && (
          <div className="mt-sd-2">
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (status === 'uploading') {
    return (
      <div className={`p-sd-2 rounded-sd-md bg-sd-surface-elevated w-full ${className}`.trim()}>
        <div className="flex items-center gap-sd-2">
          <Spinner size="sm" />
          <span className="text-sd-sm text-sd-text-secondary">{fileName || 'Uploading...'}</span>
        </div>
      </div>
    );
  }

  if (status === 'complete') {
    return (
      <div
        role="button"
        tabIndex={0}
        className={`p-sd-2 rounded-sd-md bg-sd-surface-elevated w-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${className}`.trim()}
      >
        <div className="flex items-center gap-sd-1">
          <svg className="w-4 h-4 text-sd-success shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 8.5L6.5 12L13 4" />
          </svg>
          <span className="text-sd-sm text-sd-text">{fileName}</span>
          {fileSize && <span className="text-sd-xs text-sd-text-muted">{fileSize}</span>}
        </div>
      </div>
    );
  }

  if (mode === 'download' && status === 'available') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onDownload}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onDownload?.();
          }
        }}
        className={`p-sd-2 rounded-sd-md bg-sd-surface-elevated w-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${className}`.trim()}
      >
        <div className="flex items-center gap-sd-1">
          <svg className="w-4 h-4 text-sd-primary shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 2v9M4.5 7.5L8 11l3.5-3.5M3 13h10" />
          </svg>
          <span className="text-sd-sm text-sd-text">{fileName}</span>
          {fileSize && <span className="text-sd-xs text-sd-text-muted">{fileSize}</span>}
        </div>
      </div>
    );
  }

  // Upload prompt (default)
  return (
    <div className={`p-sd-2 rounded-sd-md bg-sd-surface-elevated w-full ${className}`.trim()}>
      <label
        htmlFor={inputId}
        className="flex flex-col items-center gap-sd-1 p-sd-3 border-2 border-dashed border-sd-border rounded-sd-md cursor-pointer hover:border-sd-primary"
      >
        <svg className="w-6 h-6 text-sd-text-muted" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M8 11V2M4.5 5.5L8 2l3.5 3.5M3 13h10" />
        </svg>
        <span className="text-sd-sm text-sd-text-secondary">Drop file here or click to browse</span>
        {acceptedTypes.length > 0 && (
          <span className="text-sd-xs text-sd-text-muted">Accepted: {acceptedTypes.join(', ')}</span>
        )}
      </label>
      <input
        id={inputId}
        type="file"
        className="sr-only"
        accept={acceptedTypes.map((t) => `.${t.toLowerCase()}`).join(',')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelect?.(file);
        }}
      />
    </div>
  );
}
